const DEFAULT_STEM_GRADIENT = [0x0f5132, 0x2f855a, 0x9ae6b4]

const gradientCache = new WeakMap()

export function parseColorToNumber(color) {
  if (typeof color === 'number') {
    return color
  }
  if (typeof color === 'string') {
    const normalized = color.trim().replace(/^#/, '')
    if (normalized.length === 3) {
      const expanded = normalized.split('').map((char) => char + char).join('')
      return parseInt(expanded, 16)
    }
    const value = parseInt(normalized, 16)
    return Number.isNaN(value) ? null : value
  }
  return null
}

export function adjustColorLuminance(color, amount = 0) {
  const base = parseColorToNumber(color)
  if (base == null) return DEFAULT_STEM_GRADIENT[1]

  const r = (base >> 16) & 0xff
  const g = (base >> 8) & 0xff
  const b = base & 0xff

  const adjustChannel = (channel) => {
    if (amount >= 0) {
      return Math.min(255, Math.round(channel + (255 - channel) * amount))
    }
    return Math.max(0, Math.round(channel + channel * amount))
  }

  const newR = adjustChannel(r)
  const newG = adjustChannel(g)
  const newB = adjustChannel(b)

  return (newR << 16) | (newG << 8) | newB
}

function blendColors(start, end, t) {
  const sr = (start >> 16) & 0xff
  const sg = (start >> 8) & 0xff
  const sb = start & 0xff

  const er = (end >> 16) & 0xff
  const eg = (end >> 8) & 0xff
  const eb = end & 0xff

  const r = Math.round(sr + (er - sr) * t)
  const g = Math.round(sg + (eg - sg) * t)
  const b = Math.round(sb + (eb - sb) * t)

  return (r << 16) | (g << 8) | b
}

function getGradientStops(plantConfig) {
  if (plantConfig && gradientCache.has(plantConfig)) {
    return gradientCache.get(plantConfig)
  }

  let stops
  if (plantConfig?.colorGradient && plantConfig.colorGradient.length > 1) {
    stops = plantConfig.colorGradient
      .map((stop) => parseColorToNumber(stop))
      .filter((value) => typeof value === 'number')
  } else if (plantConfig?.color) {
    const base = parseColorToNumber(plantConfig.color)
    stops = [
      adjustColorLuminance(base, -0.3),
      base,
      adjustColorLuminance(base, 0.25),
    ]
  } else {
    stops = DEFAULT_STEM_GRADIENT
  }

  if (plantConfig) {
    gradientCache.set(plantConfig, stops)
  }
  return stops
}

export function getGradientColor(plantConfig, ratio = 0) {
  const stops = getGradientStops(plantConfig)
  if (!Array.isArray(stops) || stops.length === 0) {
    return DEFAULT_STEM_GRADIENT[1]
  }

  const clampedRatio = Math.max(0, Math.min(1, ratio))
  if (stops.length === 1) {
    return stops[0]
  }

  const scaled = clampedRatio * (stops.length - 1)
  const index = Math.floor(scaled)
  const localRatio = scaled - index
  const start = stops[index]
  const end = stops[Math.min(index + 1, stops.length - 1)]

  return blendColors(start, end, localRatio)
}
